import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { 
  Globe,
  Plus,
  Edit,
  Trash2,
  Eye,
  Code,
  Image,
  Video,
  Type,
  Layout,
  Save,
  Copy
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPageBuilder = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    content: '',
    page_type: 'landing',
    is_public: true,
    template: 'basic',
    custom_css: '',
    custom_js: ''
  });

  const pageTypes = [
    { value: 'landing', label: 'Landing Page', description: 'Member recruitment pages' },
    { value: 'product', label: 'Product Page', description: 'Product showcase pages' },
    { value: 'training', label: 'Training Page', description: 'Educational content' },
    { value: 'network', label: 'Network Page', description: 'Live streaming and announcements' }
  ];

  const templates = [
    { value: 'basic', label: 'Basic Template', description: 'Simple layout with header and content' },
    { value: 'sales', label: 'Sales Template', description: 'High-converting sales page layout' },
    { value: 'training', label: 'Training Template', description: 'Educational content layout' },
    { value: 'video', label: 'Video Template', description: 'Video-focused with chat integration' }
  ];

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/admin/pages`);
      setPages(response.data);
    } catch (error) {
      console.error('Failed to fetch pages:', error);
      // Mock data for demo
      setPages([
        {
          id: '1',
          title: 'Welcome Landing Page',
          slug: 'welcome',
          page_type: 'landing',
          is_public: true,
          created_at: new Date().toISOString(),
          view_count: 145
        },
        {
          id: '2',
          title: 'Product Showcase',
          slug: 'products',
          page_type: 'product',
          is_public: true,
          created_at: new Date().toISOString(),
          view_count: 89
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-generate slug from title
    if (field === 'title') {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 50);
      setFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPage) {
        await axios.put(`${API}/admin/pages/${editingPage.id}`, formData);
        toast.success('Page updated successfully');
      } else {
        await axios.post(`${API}/admin/pages`, formData);
        toast.success('Page created successfully');
      }
      
      setShowDialog(false);
      setEditingPage(null);
      resetForm();
      fetchPages();
    } catch (error) {
      console.error('Failed to save page:', error);
      const message = error.response?.data?.detail || 'Failed to save page';
      toast.error(message);
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      slug: page.slug,
      description: page.description || '',
      content: page.content || '',
      page_type: page.page_type,
      is_public: page.is_public,
      template: page.template || 'basic',
      custom_css: page.custom_css || '',
      custom_js: page.custom_js || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/pages/${pageId}`);
      toast.success('Page deleted successfully');
      fetchPages();
    } catch (error) {
      console.error('Failed to delete page:', error);
      toast.error('Failed to delete page');
    }
  };

  const handlePreview = (page) => {
    const previewUrl = `${window.location.origin}/page/${page.slug}`;
    window.open(previewUrl, '_blank');
  };

  const handleCopyUrl = (page) => {
    const pageUrl = `${window.location.origin}/page/${page.slug}`;
    navigator.clipboard.writeText(pageUrl);
    toast.success('Page URL copied to clipboard');
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      description: '',
      content: '',
      page_type: 'landing',
      is_public: true,
      template: 'basic',
      custom_css: '',
      custom_js: ''
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Page Builder">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Page Builder">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Member Landing Pages</h2>
          <p className="text-sm text-gray-600">
            Create and manage landing pages for your members
          </p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            resetForm();
            setEditingPage(null);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Create Page
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPage ? 'Edit Page' : 'Create New Page'}
              </DialogTitle>
              <DialogDescription>
                Build professional landing pages for your members
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Page Title</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Welcome to BBC Quick List"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="slug">URL Slug</Label>
                  <Input
                    id="slug"
                    placeholder="welcome"
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    required
                  />
                  <p className="text-xs text-gray-500">
                    Page will be accessible at: /page/{formData.slug}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="page_type">Page Type</Label>
                  <Select 
                    value={formData.page_type} 
                    onValueChange={(value) => handleInputChange('page_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select page type" />
                    </SelectTrigger>
                    <SelectContent>
                      {pageTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div>
                            <div className="font-medium">{type.label}</div>
                            <div className="text-xs text-gray-500">{type.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template">Template</Label>
                  <Select 
                    value={formData.template} 
                    onValueChange={(value) => handleInputChange('template', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((template) => (
                        <SelectItem key={template.value} value={template.value}>
                          <div>
                            <div className="font-medium">{template.label}</div>
                            <div className="text-xs text-gray-500">{template.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  placeholder="Brief description of the page"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Page Content (HTML)</Label>
                <Textarea
                  id="content"
                  placeholder="Enter your HTML content here..."
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  rows={10}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500">
                  You can use HTML, CSS, and JavaScript. Member referral codes will be automatically inserted.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="custom_css">Custom CSS</Label>
                  <Textarea
                    id="custom_css"
                    placeholder="/* Custom styles */"
                    value={formData.custom_css}
                    onChange={(e) => handleInputChange('custom_css', e.target.value)}
                    rows={6}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom_js">Custom JavaScript</Label>
                  <Textarea
                    id="custom_js"
                    placeholder="// Custom scripts"
                    value={formData.custom_js}
                    onChange={(e) => handleInputChange('custom_js', e.target.value)}
                    rows={6}
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="is_public"
                  type="checkbox"
                  checked={formData.is_public}
                  onChange={(e) => handleInputChange('is_public', e.target.checked)}
                  className="h-4 w-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                />
                <Label htmlFor="is_public" className="text-sm">
                  Make page publicly accessible
                </Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary">
                  <Save className="h-4 w-4 mr-2" />
                  {editingPage ? 'Update Page' : 'Create Page'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Page Builder Tools */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="text-center p-4 hover:shadow-lg transition-shadow cursor-pointer">
          <Layout className="h-8 w-8 mx-auto mb-2 text-blue-600" />
          <h3 className="font-semibold">Templates</h3>
          <p className="text-xs text-gray-500">Pre-built layouts</p>
        </Card>
        
        <Card className="text-center p-4 hover:shadow-lg transition-shadow cursor-pointer">
          <Type className="h-8 w-8 mx-auto mb-2 text-green-600" />
          <h3 className="font-semibold">Text Editor</h3>
          <p className="text-xs text-gray-500">Rich content editing</p>
        </Card>
        
        <Card className="text-center p-4 hover:shadow-lg transition-shadow cursor-pointer">
          <Image className="h-8 w-8 mx-auto mb-2 text-purple-600" />
          <h3 className="font-semibold">Media Library</h3>
          <p className="text-xs text-gray-500">Images and graphics</p>
        </Card>
        
        <Card className="text-center p-4 hover:shadow-lg transition-shadow cursor-pointer">
          <Video className="h-8 w-8 mx-auto mb-2 text-red-600" />
          <h3 className="font-semibold">Video Embed</h3>
          <p className="text-xs text-gray-500">YouTube, Vimeo, etc.</p>
        </Card>
      </div>

      {/* Pages List */}
      <Card>
        <CardHeader>
          <CardTitle>Created Pages</CardTitle>
          <CardDescription>
            Manage all member landing pages and content
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pages.length > 0 ? (
            <div className="space-y-4">
              {pages.map((page) => (
                <div 
                  key={page.id} 
                  className="border border-gray-200 rounded-lg p-4 hover:border-yellow-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Globe className="h-5 w-5 text-gray-600" />
                        <div>
                          <h4 className="font-semibold text-gray-900">{page.title}</h4>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <span>/page/{page.slug}</span>
                            <Badge variant="outline" className="text-xs">
                              {page.page_type}
                            </Badge>
                            {page.is_public ? (
                              <Badge className="bg-green-100 text-green-800 text-xs">Public</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">Private</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div>Created: {new Date(page.created_at).toLocaleDateString()}</div>
                        <div>Views: {page.view_count || 0}</div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreview(page)}
                        title="Preview page"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyUrl(page)}
                        title="Copy page URL"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(page)}
                        title="Edit page"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(page.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete page"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Pages Created</h3>
              <p className="text-gray-600 mb-4">
                Create your first landing page for members to use
              </p>
              <Button className="btn-primary" onClick={() => setShowDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Page
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminPageBuilder;